import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config/api';
import { authHeaders, clearToken, isSessionValid, updateLastActivity, startSessionCheck, stopSessionCheck, logout } from '../../services/auth';
import { adminTheme, adminAnimations } from '../../theme-admin';

// Global Animations
const GlobalStyle = styled.div`
  ${adminAnimations}
`;

const Shell = styled.div`
  display: grid;
  grid-template-columns: ${(p)=>p.$collapsed ? '80px 1fr' : '280px 1fr'};
  min-height: 100vh;
  background: ${adminTheme.colors.background.secondary};
  transition: grid-template-columns 300ms cubic-bezier(0.4, 0, 0.2, 1);
`;

const Sidebar = styled.aside`
  background: ${adminTheme.colors.sidebar.background};
  color: ${adminTheme.colors.sidebar.text};
  padding: ${adminTheme.spacing.lg};
  border-right: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%);
    pointer-events: none;
  }
`;

const Brand = styled.div`
  font-family: ${adminTheme.fonts.heading};
  font-weight: ${adminTheme.fontWeights.extrabold};
  font-size: ${adminTheme.fontSizes['2xl']};
  letter-spacing: -0.5px;
  margin: ${adminTheme.spacing.md} 0 ${adminTheme.spacing['2xl']};
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
  gap: ${adminTheme.spacing.md};
  position: relative;
  z-index: 1;

  &::after {
    content: '✨';
    font-size: ${adminTheme.fontSizes.lg};
    animation: pulse 2s ease-in-out infinite;
  }
`;

const BrandCollapsed = styled.div`
  font-size: ${adminTheme.fontSizes['3xl']};
  text-align: center;
  margin-bottom: ${adminTheme.spacing.xl};
`;

const NavList = styled.nav`
  display: flex;
  flex-direction: column;
  gap: ${adminTheme.spacing.sm};
  position: relative;
  z-index: 1;
`;

const SLink = styled(NavLink)`
  display: grid;
  grid-template-columns: ${props => props.$collapsed ? '1fr' : '32px 1fr'};
  align-items: center;
  gap: ${adminTheme.spacing.md};
  padding: ${adminTheme.spacing.md} ${adminTheme.spacing.lg};
  border-radius: ${adminTheme.borderRadius.xl};
  color: ${adminTheme.colors.sidebar.text};
  text-decoration: none;
  font-weight: ${adminTheme.fontWeights.semibold};
  font-size: ${adminTheme.fontSizes.base};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: ${adminTheme.transitions.normal};
  position: relative;

  &.active {
    background: ${adminTheme.colors.sidebar.active};
    color: ${adminTheme.colors.sidebar.textActive};
    box-shadow: ${adminTheme.shadows.glow};
    transform: translateX(4px);
  }

  &:hover:not(.active) {
    background: ${adminTheme.colors.sidebar.hover};
    transform: translateX(2px);
    color: #fff;
  }

  ${props => props.$collapsed && `
    justify-content: center;
    padding: ${adminTheme.spacing.md};
  `}
`;

const Icon = styled.span`
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: ${adminTheme.fontSizes.xl};
  ${props => props.$collapsed && `
    font-size: ${adminTheme.fontSizes['2xl']};
  `}
`;

const Topbar = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${adminTheme.spacing.lg} ${adminTheme.spacing.xl};
  background: ${adminTheme.colors.background.primary};
  border-bottom: 1px solid ${adminTheme.colors.gray[200]};
  box-shadow: ${adminTheme.shadows.sm};
  backdrop-filter: blur(10px);
  position: sticky;
  top: 0;
  z-index: ${adminTheme.zIndex.sticky};
`;

const Content = styled.main`
  display: flex;
  flex-direction: column;
  background: ${adminTheme.colors.background.secondary};
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${adminTheme.spacing.xs};
  padding: ${adminTheme.spacing.sm} ${adminTheme.spacing.md};
  font-size: ${adminTheme.fontSizes.sm};
  font-weight: ${adminTheme.fontWeights.semibold};
  border-radius: ${adminTheme.borderRadius.full};
  background: linear-gradient(135deg, ${adminTheme.colors.primary[50]} 0%, ${adminTheme.colors.accent[50]} 100%);
  color: ${adminTheme.colors.primary[700]};
  border: 1px solid ${adminTheme.colors.primary[200]};
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${adminTheme.spacing.sm};
  border: 1px solid ${adminTheme.colors.gray[300]};
  background: ${adminTheme.colors.background.primary};
  color: ${adminTheme.colors.gray[700]};
  padding: ${adminTheme.spacing.sm} ${adminTheme.spacing.lg};
  border-radius: ${adminTheme.borderRadius.lg};
  font-weight: ${adminTheme.fontWeights.semibold};
  cursor: pointer;
  transition: ${adminTheme.transitions.fast};
  font-size: ${adminTheme.fontSizes.sm};

  &:hover {
    background: ${adminTheme.colors.gray[50]};
    border-color: ${adminTheme.colors.gray[400]};
    transform: translateY(-1px);
    box-shadow: ${adminTheme.shadows.md};
  }

  &:active {
    transform: translateY(0);
  }
`;

const GradientButton = styled(Button)`
  background: ${adminTheme.gradients.primary};
  color: white;
  border: none;
  box-shadow: ${adminTheme.shadows.md};

  &:hover {
    filter: brightness(1.1);
    box-shadow: ${adminTheme.shadows.lg};
  }
`;

const Ghost = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: ${adminTheme.colors.gray[700]};
  border: 1px solid ${adminTheme.colors.gray[300]};
  width: 40px;
  height: 40px;
  border-radius: ${adminTheme.borderRadius.lg};
  cursor: pointer;
  transition: ${adminTheme.transitions.fast};
  font-size: ${adminTheme.fontSizes.lg};
  font-weight: ${adminTheme.fontWeights.bold};

  &:hover {
    background: ${adminTheme.colors.gray[100]};
    border-color: ${adminTheme.colors.gray[400]};
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

export default function AdminLayout() {
  const [me, setMe] = useState(null);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('admin_sidebar_collapsed') === '1'; } catch (_) { return false; }
  });
  const navigate = useNavigate();

  // Session validation on mount and route changes
  useEffect(() => {
    if (!isSessionValid()) {
      clearToken();
      navigate('/admin/login?session=expired', { replace: true });
      return;
    }
    updateLastActivity();
  }, [navigate]);

  // Session timeout check - managed by React lifecycle
  useEffect(() => {
    const handleSessionExpired = () => {
      navigate('/admin/login?session=expired', { replace: true });
    };

    startSessionCheck(handleSessionExpired);

    return () => {
      stopSessionCheck();
    };
  }, [navigate]);

  // User activity tracking - update activity on any interaction
  useEffect(() => {
    const handleActivity = () => {
      if (isSessionValid()) {
        updateLastActivity();
      }
    };

    // Throttled mousemove handler - only fire once per minute to avoid excessive localStorage writes
    let lastMouseMoveUpdate = 0;
    const MOUSE_MOVE_THROTTLE = 60000; // 1 minute
    
    const handleMouseMove = () => {
      const now = Date.now();
      if (now - lastMouseMoveUpdate >= MOUSE_MOVE_THROTTLE) {
        lastMouseMoveUpdate = now;
        handleActivity();
      }
    };

    // Track mouse movements (throttled), clicks, and keyboard activity
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('click', handleActivity);
    window.addEventListener('keydown', handleActivity);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('click', handleActivity);
      window.removeEventListener('keydown', handleActivity);
    };
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/auth/me`, { headers: authHeaders() });
        if (res.ok) {
          const data = await res.json();
          setMe(data);
        } else if (res.status === 401) {
          // Unauthorized - session might be invalid
          clearToken();
          navigate('/admin/login?session=expired', { replace: true });
        }
      } catch (_) { /* ignore */ }
    };
    load();
  }, [navigate]);

  const onLogout = () => {
    logout();
    navigate('/admin/login', { replace: true });
  };

  const toggleSidebar = () => {
    const next = !collapsed;
    setCollapsed(next);
    try { localStorage.setItem('admin_sidebar_collapsed', next ? '1' : '0'); } catch (_) {}
  };

  return (
    <GlobalStyle>
      <Shell $collapsed={collapsed}>
        <Sidebar>
          {collapsed ? (
            <BrandCollapsed>🚀</BrandCollapsed>
          ) : (
            <Brand>ServiceHub</Brand>
          )}
          <NavList>
            <SLink end to="/admin" title="Dashboard" $collapsed={collapsed}>
              <Icon $collapsed={collapsed}>📊</Icon>
              {!collapsed && 'Dashboard'}
            </SLink>
            <SLink to="/admin/courses" title="Courses" $collapsed={collapsed}>
              <Icon $collapsed={collapsed}>📚</Icon>
              {!collapsed && 'Courses'}
            </SLink>
            <SLink to="/admin/blogs" title="Blogs" $collapsed={collapsed}>
              <Icon $collapsed={collapsed}>📝</Icon>
              {!collapsed && 'Blogs'}
            </SLink>
            <SLink to="/admin/partners" title="Partners" $collapsed={collapsed}>
              <Icon $collapsed={collapsed}>🏢</Icon>
              {!collapsed && 'Partners'}
            </SLink>
            <SLink to="/admin/hero-slides" title="Hero Slides" $collapsed={collapsed}>
              <Icon $collapsed={collapsed}>🎭</Icon>
              {!collapsed && 'Hero Slides'}
            </SLink>
            <SLink to="/admin/testimonials" title="Testimonials" $collapsed={collapsed}>
              <Icon $collapsed={collapsed}>💬</Icon>
              {!collapsed && 'Testimonials'}
            </SLink>
            <SLink to="/admin/users" title="Users" $collapsed={collapsed}>
              <Icon $collapsed={collapsed}>👥</Icon>
              {!collapsed && 'Users'}
            </SLink>
            <SLink to="/admin/contact-forms" title="Contact Forms" $collapsed={collapsed}>
              <Icon $collapsed={collapsed}>✉️</Icon>
              {!collapsed && 'Contact Forms'}
            </SLink>
            <SLink to="/admin/settings" title="Settings" $collapsed={collapsed}>
              <Icon $collapsed={collapsed}>⚙️</Icon>
              {!collapsed && 'Settings'}
            </SLink>
          </NavList>
        </Sidebar>
        <Content>
          <Topbar>
            <div style={{ display: 'flex', alignItems: 'center', gap: adminTheme.spacing.md }}>
              <Ghost onClick={toggleSidebar} aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
                {collapsed ? '»' : '«'}
              </Ghost>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: adminTheme.spacing.md }}>
              {me && (
                <Badge>
                  <span>👤</span>
                  <span>{me.email || me.username || 'Admin'}</span>
                  {me.role && <span>• {me.role}</span>}
                </Badge>
              )}
              <GradientButton onClick={onLogout}>
                <span>🚪</span>
                Logout
              </GradientButton>
            </div>
          </Topbar>
          <div style={{ padding: adminTheme.spacing.xl }}>
            <Outlet />
          </div>
        </Content>
      </Shell>
    </GlobalStyle>
  );
}
